import { useEffect, useState } from "react";
import { Navbar } from "../components/Navbar";
import { ProfileCard } from "../components/Profile/ProfileCard";
import { ProfileTabs } from "../components/Profile/ProfileTabs";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { getListingsByUser } from "../api/listingApi";

const Profile = () => {
  const { currentUser, isAuthenticated, loading } = useAuth();
  const [profileLoading, setProfileLoading] = useState(true);
  const [userListings, setUserListings] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserListings = async () => {
      if (!loading && isAuthenticated && currentUser) {
        try {
          const response = await getListingsByUser(currentUser._id);
          if (response.success) {
            setUserListings(response.listings);
          } else {
            console.error("Failed to fetch user listings:", response.error);
          }
        } catch (error) {
          console.error("Error fetching user listings:", error);
        }
      }
    };

    fetchUserListings();
  }, [loading, isAuthenticated, currentUser]);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate("/auth");
      return;
    }
    if (!loading) {
      setProfileLoading(false);
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-24 pb-16 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading profile...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!isAuthenticated || !currentUser) {
    return null;
  }

  /**
   * Get a display string for the user's location.
   * The new schema gives us `label` directly, so this is much simpler.
   */
  const formatLocationDisplay = (location) => {
    if (!location) return "Location not set";
    if (location.label) return location.label;
    // Fallback if reverse-geocoding failed but we have structured parts
    if (location.addressParts) {
      const { suburb, city, country } = location.addressParts;
      const parts = [suburb, city, country].filter(Boolean);
      if (parts.length > 0) return parts.join(", ");
    }
    return "Location not set";
  };

  const userData = {
    id: currentUser._id,
    name: currentUser.firstName + " " + currentUser.lastName,
    avatar: currentUser.image || "",
    location: formatLocationDisplay(currentUser.location),
    memberSince: new Date(currentUser.createdAt).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    }),
    bio: currentUser.bio || "No bio available yet",
    rating: currentUser.rating || 0,
    listingCount: userListings.length || 0,
    exchangesCount: currentUser.exchangeCount || 0,
    isCurrentUser: true,
    isFoundingMember: currentUser.isFoundingMember,
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-24 pb-16">
        <div className="container px-4 mx-auto">
          <div className="mb-12 animate-fade-in">
            <ProfileCard {...userData} />
          </div>
          <ProfileTabs listings={userListings} isCurrentUser={true} />
        </div>
      </main>

      <footer className="bg-sage-50 py-8 border-t border-border/60">
        <div className="container px-4 mx-auto text-center text-sm text-muted-foreground">
          &copy; {new Date().getFullYear()} Thoreau Consulting (PTY) Ltd. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Profile;